import React, { useState, useEffect } from "react";
import { api } from "../utils/api";
import { useI18n } from "../contexts/I18nContext";

const PLAN_DISPLAY = {
  free: { icon: "🌿", color: "text-sensoria-text-dim", border: "border-sensoria-border", bg: "" },
  premium: { icon: "💜", color: "text-sensoria-accent-light", border: "border-sensoria-accent/50", bg: "sensoria-glow" },
  vip: { icon: "👑", color: "text-amber-400", border: "border-amber-500/50", bg: "shadow-lg shadow-amber-500/10" },
};

export default function Premium() {
  const { t } = useI18n();
  const [plans, setPlans] = useState(null);
  const [currentPlan, setCurrentPlan] = useState("free");
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingOut, setCheckingOut] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const [plansData, statusData] = await Promise.all([
          api("/api/premium/plans"),
          api("/api/premium/status"),
        ]);
        setPlans(plansData.plans);
        setCurrentPlan(statusData.plan);
        setSubscription(statusData.subscription);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  async function handleCheckout(plan) {
    setCheckingOut(plan);
    setError("");
    try {
      const data = await api("/api/premium/checkout", {
        method: "POST",
        body: JSON.stringify({ plan }),
      });
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
      setCheckingOut(null);
    }
  }

  async function handlePortal() {
    try {
      const data = await api("/api/premium/portal", { method: "POST" });
      window.location.href = data.url;
    } catch (err) {
      setError(err.message);
    }
  }

  // Check URL params for success/cancel
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("success")) {
      // Refresh status
      api("/api/premium/status").then((data) => {
        setCurrentPlan(data.plan);
        setSubscription(data.subscription);
      });
    }
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-sensoria-accent border-t-transparent" />
      </div>
    );
  }

  const searchSuccess = new URLSearchParams(window.location.search).get("success");

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h2 className="font-display text-3xl font-bold text-white">{t("premium_title")}</h2>
        <p className="mt-2 text-sensoria-text-dim">
          {t("premium_desc")}
        </p>
      </div>

      {searchSuccess && (
        <div className="rounded-xl border border-sensoria-success/30 bg-sensoria-success/10 p-4 text-center text-sm text-sensoria-success animate-slide-up">
          🎉 Bienvenue dans le plan {currentPlan} ! Profitez de toutes vos nouvelles fonctionnalités.
        </div>
      )}

      {error && (
        <div className="rounded-lg border border-sensoria-danger/30 bg-sensoria-danger/10 px-4 py-3 text-sm text-sensoria-danger">
          {error}
        </div>
      )}

      {/* Plans grid */}
      {plans && (
        <div className="grid gap-6 sm:grid-cols-3">
          {Object.entries(plans).map(([key, plan]) => {
            const display = PLAN_DISPLAY[key] || PLAN_DISPLAY.free;
            const isCurrent = key === currentPlan;

            return (
              <div
                key={key}
                className={`relative rounded-2xl border p-6 transition-all ${display.border} ${display.bg} ${
                  isCurrent ? "ring-2 ring-sensoria-accent" : ""
                }`}
              >
                {isCurrent && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-sensoria-accent px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white">
                    Actuel
                  </span>
                )}

                <div className="mb-4 text-center">
                  <span className="text-3xl">{display.icon}</span>
                  <h3 className={`mt-2 font-display text-xl font-bold ${display.color}`}>
                    {plan.name}
                  </h3>
                  <div className="mt-1">
                    {plan.price === 0 ? (
                      <span className="text-2xl font-bold text-white">Gratuit</span>
                    ) : (
                      <div>
                        <span className="text-3xl font-bold text-white">{plan.price}€</span>
                        <span className="text-sm text-sensoria-muted">/mois</span>
                      </div>
                    )}
                  </div>
                </div>

                <ul className="mb-6 space-y-2">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <span className="mt-0.5 text-sensoria-success">✓</span>
                      <span className="text-sensoria-text-dim">{feature}</span>
                    </li>
                  ))}
                </ul>

                {isCurrent ? (
                  key !== "free" ? (
                    <button
                      onClick={handlePortal}
                      className="w-full rounded-xl border border-sensoria-border px-4 py-2.5 text-sm font-medium text-sensoria-text-dim transition-colors hover:border-sensoria-muted hover:text-white"
                    >
                      Gérer l'abonnement
                    </button>
                  ) : null
                ) : key !== "free" ? (
                  <button
                    onClick={() => handleCheckout(key)}
                    disabled={checkingOut === key}
                    className={`w-full rounded-xl px-4 py-2.5 text-sm font-semibold transition-all ${
                      key === "vip"
                        ? "bg-gradient-to-r from-amber-500 to-amber-600 text-black hover:from-amber-400 hover:to-amber-500 shadow-lg shadow-amber-500/20"
                        : "sensoria-btn"
                    }`}
                  >
                    {checkingOut === key ? (
                      <span className="flex items-center justify-center gap-2">
                        <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                        Redirection...
                      </span>
                    ) : (
                      `Choisir ${plan.name}`
                    )}
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      {/* Current subscription info */}
      {subscription && currentPlan !== "free" && (
        <div className="sensoria-card text-center">
          <p className="text-sm text-sensoria-text-dim">
            Votre abonnement {plans?.[currentPlan]?.name} est actif jusqu'au{" "}
            <span className="font-semibold text-white">
              {new Date(subscription.current_period_end).toLocaleDateString("fr-FR", {
                day: "numeric", month: "long", year: "numeric",
              })}
            </span>
          </p>
        </div>
      )}

      <p className="text-center text-xs text-sensoria-muted">
        {t("premium_footer")}
      </p>
    </div>
  );
}
